import { Delete } from '@mui/icons-material';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { BlockedDate } from '../hooks/useCalendarData';

interface BlockedDateFormData {
    startDate: string;
    endDate: string;
    blockType: 'maintenance' | 'personal';
    reason: string;
}

interface PrefilledDates {
    startDate: string;
    endDate: string;
}

interface BlockedDateDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: BlockedDateFormData) => Promise<void>;
    onDelete?: () => void;
    editingBlockedDate?: BlockedDate | null;
    prefilledDates?: PrefilledDates;
    loading?: boolean;
    error?: string | null;
}

const initialFormData: BlockedDateFormData = {
    startDate: '',
    endDate: '',
    blockType: 'maintenance',
    reason: '',
};

const BlockedDateDialog = ({
    open,
    onClose,
    onSubmit,
    onDelete,
    editingBlockedDate,
    prefilledDates,
    loading = false,
    error = null,
}: BlockedDateDialogProps) => {
    const [formData, setFormData] = useState<BlockedDateFormData>(initialFormData);
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (editingBlockedDate) {
            setFormData({
                startDate: editingBlockedDate.startDate,
                endDate: editingBlockedDate.endDate,
                blockType: editingBlockedDate.blockType,
                reason: editingBlockedDate.reason ?? '',
            });
        } else if (prefilledDates) {
            setFormData({
                ...initialFormData,
                startDate: prefilledDates.startDate,
                endDate: prefilledDates.endDate,
            });
        } else {
            setFormData(initialFormData);
        }
        setValidationError(null);
    }, [editingBlockedDate, prefilledDates, open]);

    const handleSubmit = async () => {
        // Client-side validation
        if (!formData.startDate || !formData.endDate) {
            setValidationError('Start date and end date are required');
            return;
        }

        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            setValidationError('End date must be on or after start date');
            return;
        }

        setValidationError(null);
        await onSubmit(formData);
    };

    const handleClose = () => {
        setFormData(initialFormData);
        setValidationError(null);
        onClose();
    };

    const isEditing = Boolean(editingBlockedDate);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '4px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    fontWeight: 900,
                    fontSize: '1.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 2.5,
                }}
            >
                {isEditing ? 'Edit Blocked Date' : 'Add New Blocked Date'}
            </DialogTitle>
            <DialogContent
                sx={{
                    backgroundColor: '#1a1a2e',
                    p: 0,
                }}
            >
                <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                    {(validationError ?? error) && (
                        <Alert
                            severity="error"
                            onClose={() => setValidationError(null)}
                            sx={{
                                borderRadius: 0,
                                border: '3px solid #ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': {
                                    color: '#ef4444',
                                },
                            }}
                        >
                            {validationError ?? error}
                        </Alert>
                    )}

                    <TextField
                        label="Start Date"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                startDate: e.target.value,
                            })
                        }
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                        required
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& input': {
                                    backgroundColor: '#0f0f23',
                                },
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />

                    <TextField
                        label="End Date"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                endDate: e.target.value,
                            })
                        }
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                        required
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& input': {
                                    backgroundColor: '#0f0f23',
                                },
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />

                    <FormControl
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    >
                        <InputLabel id="block-type-label">Block Type</InputLabel>
                        <Select
                            labelId="block-type-label"
                            id="block-type-select"
                            value={formData.blockType}
                            label="Block Type"
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    blockType: e.target.value as 'maintenance' | 'personal',
                                })
                            }
                            sx={{
                                backgroundColor: '#0f0f23',
                            }}
                        >
                            <MenuItem value="maintenance">Maintenance</MenuItem>
                            <MenuItem value="personal">Personal</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Reason (Optional)"
                        value={formData.reason}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                reason: e.target.value,
                            })
                        }
                        fullWidth
                        multiline
                        rows={3}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& textarea': {
                                    backgroundColor: '#0f0f23',
                                },
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: '3px solid #374151',
                    backgroundColor: '#0f0f23',
                    p: 3,
                    gap: 1.5,
                    justifyContent: isEditing && onDelete ? 'space-between' : 'flex-end',
                }}
            >
                {isEditing && onDelete && (
                    <Button
                        onClick={onDelete}
                        disabled={loading}
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: 'error.main',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            },
                        }}
                    >
                        Delete
                    </Button>
                )}
                <div style={{ display: 'flex', gap: '12px' }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#374151',
                                backgroundColor: 'rgba(55, 65, 81, 0.1)',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!formData.startDate || !formData.endDate || loading}
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                        }}
                    >
                        {isEditing ? 'Update' : 'Create'}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default BlockedDateDialog;
